import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Headset, History, ArrowLeft, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WELCOME = { role: "assistant", content: "Welcome to Castles Creation! We're here to help before and after you move to the UK. How can we help you today?" };
const NUDGE_TEXT = "We'd love to help you properly. To give you personalised guidance and save your support history, please become a free Castles Member. Membership is completely free and only takes around 2 minutes.";
const TOKEN = "[SUGGEST_LIVE_SUPPORT]";

export default function SupportWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [escalate, setEscalate] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", question: "" });
  const [pastChats, setPastChats] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (user) {
      api.get("/support/chats").then((r) => setPastChats(r.data)).catch(() => {});
      setForm((f) => ({
        ...f,
        name: f.name || user.first_name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || (user.whatsapp ? `${user.whatsapp_country_code || ""} ${user.whatsapp}`.trim() : ""),
      }));
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem("cc_support_guest") || "null");
        if (saved?.length > 1) setMessages(saved);
      } catch {}
    }
  }, [open, user]);

  useEffect(() => {
    if (!user && messages.length > 1) {
      localStorage.setItem("cc_support_guest", JSON.stringify(messages.slice(-30)));
    }
  }, [messages, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, escalate, showForm, showHistory]);

  const resetChat = () => {
    setMessages([WELCOME]);
    setChatId(null);
    setEscalate(false);
    setShowForm(false);
    setRequestSent(false);
    setShowHistory(false);
    if (!user) localStorage.removeItem("cc_support_guest");
  };

  const openChat = async (id) => {
    try {
      const res = await api.get(`/support/chats/${id}`);
      setChatId(id);
      setMessages(res.data.messages.map((m) => ({ role: m.role, content: m.content })));
      setShowHistory(false);
    } catch {
      setShowHistory(false);
    }
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setEscalate(false);
    setShowForm(false);
    setRequestSent(false);
    setForm((f) => ({ ...f, question: message }));
    const wasFirst = !messages.some((m) => m.role === "user");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const headers = { "Content-Type": "application/json" };
      const t = localStorage.getItem("cc_token");
      if (t) headers.Authorization = `Bearer ${t}`;
      const res = await fetch(`${API}/support/chat`, {
        method: "POST", headers, credentials: "include",
        body: JSON.stringify({ message, chat_id: chatId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.delta) {
              acc += ev.delta;
              const clean = acc.replace(TOKEN, "");
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: clean };
                return copy;
              });
            }
            if (ev.chat_id) setChatId(ev.chat_id);
            if (ev.suggest) setEscalate(true);
          } catch {}
        }
      }
      if (!user && wasFirst) {
        setMessages((m) => [...m, { role: "assistant", content: NUDGE_TEXT, nudge: true }]);
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting right now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
      if (user) api.get("/support/chats").then((r) => setPastChats(r.data)).catch(() => {});
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support/request", { name: form.name, email: form.email, phone: form.phone, question: form.question });
      setShowForm(false);
      setEscalate(false);
      setRequestSent(true);
      setMessages((m) => [...m, { role: "assistant", content: "Your live support request has been sent to the Castles team. We'll get back to you as soon as possible." }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Something went wrong sending your request. Please check your email address and try again." }]);
    }
  };

  const FormInput = (props) => (
    <input {...props} className="w-full bg-[#FDFDFC] border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#D4AF37]" />
  );

  return (
    <>
      <button
        data-testid="support-widget-button"
        onClick={() => setOpen(!open)}
        aria-label="Support"
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex items-center gap-2.5 rounded-full bg-[#0A1633] text-white pl-4 pr-5 py-3.5 shadow-[0_12px_32px_rgb(10,22,51,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgb(10,22,51,0.45)] focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
      >
        <MessageCircle className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
        <span className="text-sm font-medium">Need Help?</span>
      </button>

      {open && (
        <div
          data-testid="support-chat-window"
          className="fixed bottom-[4.5rem] right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[400px] h-[min(600px,72vh)] bg-white rounded-3xl shadow-[0_24px_60px_rgb(10,22,51,0.25)] border border-slate-100 flex flex-col overflow-hidden"
        >
          <div className="bg-[#0A1633] px-5 py-4 flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Castles Support</p>
              <p className="text-xs text-[#10B981] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Typically replies instantly</p>
            </div>
            {user && pastChats.length > 0 && !showHistory && (
              <button data-testid="support-history-button" onClick={() => setShowHistory(true)} aria-label="Past conversations"
                className="text-slate-400 transition-colors duration-300 hover:text-[#D4AF37] p-1.5">
                <History className="w-5 h-5" strokeWidth={1.8} />
              </button>
            )}
            <button data-testid="support-close-button" onClick={() => setOpen(false)} aria-label="Close support"
              className="text-slate-400 transition-colors duration-300 hover:text-white p-1.5">
              <X className="w-5 h-5" />
            </button>
          </div>

          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-5 bg-[#FDFDFC]" data-testid="support-history-list">
              <button onClick={() => setShowHistory(false)} className="flex items-center gap-2 text-sm text-slate-500 transition-colors duration-300 hover:text-[#0A1633] mb-4">
                <ArrowLeft className="w-4 h-4" /> Back to chat
              </button>
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-3">Past conversations</p>
              <div className="space-y-2">
                {pastChats.map((c) => (
                  <button key={c.chat_id} data-testid={`support-history-${c.chat_id}`} onClick={() => openChat(c.chat_id)}
                    className="w-full text-left bg-white border border-slate-100 rounded-2xl px-4 py-3.5 transition-all duration-300 hover:border-[#D4AF37]/50 hover:-translate-y-0.5">
                    <p className="text-sm font-medium text-[#0A1633] truncate">{c.title}</p>
                    <p className="text-xs text-slate-400 mt-1">{(c.updated_at || "").slice(0, 10)}</p>
                  </button>
                ))}
              </div>
              <button data-testid="support-new-chat-button" onClick={resetChat}
                className="mt-4 w-full rounded-full bg-[#0A1633] text-white text-sm font-medium py-3 transition-all duration-300 hover:-translate-y-0.5">
                Start new conversation
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDFDFC]" data-testid="support-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-[#0A1633] text-white rounded-br-md" : "bg-white border border-slate-100 text-slate-700 rounded-bl-md"
                    }`}>
                      {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-[#D4AF37] animate-pulse rounded" /> : "")}
                      {m.nudge && (
                        <div className="mt-3 space-y-2">
                          <button data-testid="support-nudge-join" onClick={() => navigate("/onboarding")}
                            className="w-full rounded-full bg-[#D4AF37] text-[#0A1633] font-semibold text-sm py-2.5 transition-all duration-300 hover:bg-[#e5c558]">
                            Become a Free Member
                          </button>
                          <button data-testid="support-nudge-login" onClick={() => navigate("/login")}
                            className="w-full rounded-full border border-slate-200 text-slate-600 text-sm py-2.5 transition-colors duration-300 hover:border-[#0A1633] hover:text-[#0A1633]">
                            Already a Member? Log In
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {escalate && !showForm && !requestSent && (
                  <div className="bg-[#0A1633] rounded-2xl p-5" data-testid="support-escalation-prompt">
                    <div className="flex items-start gap-3">
                      <Headset className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.8} />
                      <div>
                        <p className="text-sm text-white font-medium">This sounds like something our team can help you with personally.</p>
                        <p className="text-xs text-slate-300 mt-1">Would you like to request live support?</p>
                        <div className="mt-3 flex gap-2">
                          <button data-testid="support-escalate-yes" onClick={() => setShowForm(true)}
                            className="rounded-full bg-[#D4AF37] text-[#0A1633] text-xs font-semibold px-5 py-2 transition-colors duration-300 hover:bg-[#e5c558]">
                            Yes, request live support
                          </button>
                          <button data-testid="support-escalate-no" onClick={() => setEscalate(false)}
                            className="rounded-full border border-white/20 text-slate-300 text-xs px-4 py-2 transition-colors duration-300 hover:text-white">
                            Not now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showForm && (
                  <form onSubmit={submitRequest} className="bg-white border border-[#D4AF37]/40 rounded-2xl p-5 space-y-3" data-testid="support-request-form">
                    <p className="text-sm font-semibold text-[#0A1633]">Request live support</p>
                    <FormInput data-testid="support-form-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <FormInput data-testid="support-form-email" type="email" required placeholder="Email address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <FormInput data-testid="support-form-phone" placeholder="Phone / WhatsApp (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <textarea data-testid="support-form-question" required rows={3} placeholder="How can we help? *" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
                      className="w-full bg-[#FDFDFC] border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#D4AF37]" />
                    <button data-testid="support-request-submit" type="submit"
                      className="w-full rounded-full bg-[#0A1633] text-white text-sm font-medium py-3 transition-all duration-300 hover:-translate-y-0.5">
                      Send request
                    </button>
                  </form>
                )}

                {requestSent && (
                  <div className="flex items-center gap-2.5 text-[#10B981] text-sm font-medium" data-testid="support-request-sent">
                    <CheckCircle2 className="w-5 h-5" /> Request sent to the Castles team
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 bg-white flex items-center gap-2 shrink-0" data-testid="support-chat-form">
                <input
                  data-testid="support-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question..."
                  className="flex-1 bg-[#FDFDFC] border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#D4AF37]"
                />
                <button data-testid="support-chat-send" type="submit" disabled={streaming || !input.trim()}
                  className="w-10 h-10 rounded-full bg-[#0A1633] flex items-center justify-center transition-all duration-300 hover:bg-[#16224a] disabled:opacity-40">
                  <Send className="w-4 h-4 text-[#D4AF37]" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
